import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCloud, IconPlugConnected, IconUsb } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  AdbConnectionProvider,
  useAdbConnection,
} from "@components/wifi_setup/AdbConnectionContext";
import StatusAlert, { Status } from "@components/wifi_setup/StatusAlert";
import { ConfigKeyOption, getConfigKeyOptions, normalizeConfigKey } from "@utils/deviceConfigKeys";
import { provisionApiKeyConfig } from "@utils/webusb-utils/ADBUtils";
import { adbErrorText } from "@utils/webusb-utils/adbErrors";

type ConfigMode = "online" | "usb";

interface ApiConfigOverrideControlProps {
  configKey: ConfigKeyOption;
  onlineSupported: boolean;
  onlineWorking: boolean;
  onlineUnsupportedNote: React.ReactNode;
  expectedSerial?: string | null;
  onConfigKeyChange: (value: ConfigKeyOption) => void;
  onApplyOnline: () => void;
}

const ModeLabel: React.FC<{
  icon: React.ReactNode;
  label: string;
  tooltip: string;
}> = ({ icon, label, tooltip }) => (
  <Tooltip label={tooltip} withArrow multiline maw={360}>
    <Group component="span" gap={6} wrap="nowrap">
      {icon}
      <span>{label}</span>
    </Group>
  </Tooltip>
);

const ApiConfigOverrideContent: React.FC<ApiConfigOverrideControlProps> = ({
  configKey,
  onlineSupported,
  onlineWorking,
  onlineUnsupportedNote,
  expectedSerial,
  onConfigKeyChange,
  onApplyOnline,
}) => {
  const [mode, setMode] = useState<ConfigMode>("online");
  const [usbConfigKey, setUsbConfigKey] = useState<ConfigKeyOption>("unset");
  const [usbWorking, setUsbWorking] = useState(false);
  const [status, setStatus] = useState<Status>(null);
  const { connected, connecting, model, serial, logs, connect, log } = useAdbConnection();
  const { t } = useTranslation();
  const configKeyOptions = useMemo(() => getConfigKeyOptions(t), [t]);
  const logText = useMemo(() => logs.join("\n"), [logs]);

  const applyOverUsb = async () => {
    if (
      expectedSerial?.trim() &&
      serial.trim() &&
      expectedSerial.trim().toLowerCase() !== serial.trim().toLowerCase()
    ) {
      setStatus({
        variant: "error",
        message: t("DeviceConfig.usbSerialMismatch", { expected: expectedSerial, actual: serial }),
      });
      return;
    }

    setUsbWorking(true);
    setStatus(null);
    try {
      await provisionApiKeyConfig(usbConfigKey, log);
      setStatus({
        variant: "success",
        message: t("DeviceConfig.usbConfigSent"),
      });
    } catch (error) {
      setStatus({ variant: "error", message: adbErrorText(error) });
    } finally {
      setUsbWorking(false);
    }
  };

  return (
    <Stack gap="sm">
      <SegmentedControl
        value={mode}
        onChange={(value) => {
          setMode(value as ConfigMode);
          setStatus(null);
        }}
        data={[
          {
            value: "online",
            label: (
              <ModeLabel
                icon={<IconCloud size={15} />}
                label={t("DeviceConfig.online")}
                tooltip={t("DeviceConfig.onlineTooltip")}
              />
            ),
          },
          {
            value: "usb",
            label: (
              <ModeLabel
                icon={<IconUsb size={15} />}
                label={t("DeviceConfig.usb")}
                tooltip={t("DeviceConfig.usbTooltip")}
              />
            ),
          },
        ]}
        w={260}
        data-testid="api-config-mode"
      />

      {mode === "online" ? (
        <Stack gap="sm">
          <Alert color="blue" variant="light" title={t("DeviceConfig.onlineNoticeTitle")}>
            {t("DeviceConfig.onlineNotice")}
          </Alert>
          {onlineSupported ? (
            <Group align="end" wrap="wrap" gap="sm">
              <Select
                label={t("DeviceConfig.configuration")}
                data={configKeyOptions}
                value={configKey}
                onChange={(value) => onConfigKeyChange(normalizeConfigKey(value))}
                allowDeselect={false}
                w={260}
                data-testid="api-config-online-select"
              />
              <Button
                onClick={onApplyOnline}
                loading={onlineWorking}
                data-testid="api-config-online-apply"
              >
                {t("DeviceConfig.apply")}
              </Button>
            </Group>
          ) : (
            onlineUnsupportedNote
          )}
        </Stack>
      ) : (
        <Stack gap="sm">
          <Group gap="sm" wrap="wrap" align="center">
            <Badge
              color={connected ? "green" : "gray"}
              variant={connected ? "filled" : "light"}
              size="lg"
              radius="sm"
              leftSection={<IconUsb size={12} />}
            >
              {connected ? t("DeviceConfig.connected") : t("DeviceConfig.notConnected")}
            </Badge>
            <Button
              onClick={connect}
              loading={connecting}
              leftSection={<IconPlugConnected size={16} />}
              variant={connected ? "light" : "filled"}
              data-testid="api-config-usb-connect"
            >
              {connected ? t("DeviceConfig.reconnect") : t("DeviceConfig.connectHeadset")}
            </Button>
            {connected && (model || serial) && (
              <Text size="sm" c="dimmed">
                {[model, serial].filter(Boolean).join(" / ")}
              </Text>
            )}
          </Group>

          {!connected ? (
            <Text size="xs" c="dimmed">
              {t("DeviceConfig.connectHint")}
            </Text>
          ) : (
            <Group align="end" wrap="wrap" gap="sm">
              <Select
                label={t("DeviceConfig.configuration")}
                data={configKeyOptions}
                value={usbConfigKey}
                onChange={(value) => setUsbConfigKey(normalizeConfigKey(value))}
                allowDeselect={false}
                w={260}
                disabled={usbWorking}
                data-testid="api-config-usb-select"
              />
              <Button
                onClick={applyOverUsb}
                loading={usbWorking}
                data-testid="api-config-usb-apply"
              >
                {t("DeviceConfig.apply")}
              </Button>
            </Group>
          )}

          <StatusAlert status={status} onClose={() => setStatus(null)} />

          {logs.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                {t("DeviceConfig.usbLogs")}
              </Text>
              <ScrollArea style={{ height: 160 }} scrollbarSize={8}>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    fontSize: 12,
                    margin: 0,
                    color: "var(--oculera-text-secondary)",
                  }}
                >
                  {logText}
                </pre>
              </ScrollArea>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const ApiConfigOverrideControl: React.FC<ApiConfigOverrideControlProps> = (props) => (
  <AdbConnectionProvider>
    <ApiConfigOverrideContent {...props} />
  </AdbConnectionProvider>
);

export default ApiConfigOverrideControl;
